import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Plus, Trash2, Edit, TrendingUp, Package, DollarSign, Users, Moon, Sun, ArrowUp, ArrowDown, GripVertical, Sparkles, LayoutGrid } from 'lucide-react';
import AdminPromotionsPanel from './AdminPromotionsPanel';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 👇 NEW: Component for a Draggable Menu Item
function SortableMenuItem({ item, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-start sm:items-center justify-between gap-3 p-3 sm:p-4 border border-slate-200/70 dark:border-slate-800 rounded-2xl bg-white/90 dark:bg-slate-950/80 backdrop-blur-sm shadow-sm hover:shadow-md transition-all mb-3"
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Drag Handle */}
        <div {...attributes} {...listeners} className="cursor-grab touch-none p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
          <GripVertical className="h-5 w-5 text-slate-400" />
        </div>

        {/* Item Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{item.title}</h3>
            <span className={`text-xs px-2 py-0.5 rounded ${item.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {item.available ? 'Available' : 'Unavailable'}
            </span>
          </div>
          <p className="text-sm text-slate-600 truncate max-w-md">{item.description}</p>
          <div className="flex items-center gap-2 text-sm">
            <p className="font-bold text-slate-900">₹{item.price}</p>
            {item.actual_price && item.actual_price > item.price && (
              <p className="line-through text-slate-500">₹{item.actual_price}</p>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 ml-4">
        <button
          onClick={() => onEdit(item)}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-2 hover:bg-red-100 text-red-600 rounded-lg"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('menu');

  // State for categories
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });

  // State for menu items
  const [menuItems, setMenuItems] = useState([]);
  const [newItem, setNewItem] = useState({
    category_id: '',
    title: '',
    description: '',
    price: '',
    actual_price: '',
    image_url: ''
  });
  const [editingItem, setEditingItem] = useState(null);

  // State for finance
  const [financeRecords, setFinanceRecords] = useState([]);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [newFinance, setNewFinance] = useState({ type: 'investment', amount: '', description: '' });

  // State for stock
  const [stockItems, setStockItems] = useState([]);
  const [stockAlerts, setStockAlerts] = useState({ empty: [], low_stock: [], reorder_needed: [] });
  const [newStock, setNewStock] = useState({ item_name: '', quantity: '', unit: '', reorder_level: '', price_per_unit: '' });

  // State for roles
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState({ role_name: '', permissions: '' });

  // State for users
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'cashier' });

  // State for orders
  const [allOrders, setAllOrders] = useState([]);

  // State for analytics
  const [analytics, setAnalytics] = useState({ most_ordered: [], most_favorited: [] });
  const [analyticsHistory, setAnalyticsHistory] = useState([]);

  // State for surveys
  const [surveys, setSurveys] = useState([]);
  const [newSurvey, setNewSurvey] = useState({ title: '', questions: [''] });
  const [surveyResponses, setSurveyResponses] = useState({});
  const [shopOpen, setShopOpen] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchAllData();
  }, [user, navigate]);

  const fetchAllData = () => {
    fetchCategories();
    fetchMenuItems();
    fetchFinance();
    fetchStock();
    fetchRoles();
    fetchUsers();
    fetchAllOrders();
    fetchAnalytics();
    fetchAnalyticsHistory();
    fetchSurveys();
    fetchShopStatus();
  };

  const fetchShopStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/shop-status`);
      setShopOpen(response.data?.shop_open !== false);
    } catch (error) {
      console.error('Failed to fetch shop status:', error);
    }
  };

  const updateShopStatus = async (nextValue) => {
    try {
      await axios.put(`${API}/admin/shop-status`, { shop_open: nextValue });
      setShopOpen(nextValue);
      toast.success(`Shop is now ${nextValue ? 'open' : 'closed'}`);
    } catch (error) {
      toast.error('Failed to update shop status');
    }
  };

  // Category functions
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, newCategory);
      toast.success('Category added successfully');
      setNewCategory({ name: '', description: '' });
      fetchCategories();
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Menu item functions
  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API}/menu-items`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to fetch menu items:', error);
    }
  };

  const handleAddMenuItem = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/menu-items`, {
        ...newItem,
        price: parseFloat(newItem.price),
        actual_price: newItem.actual_price ? parseFloat(newItem.actual_price) : null
      });
      toast.success('Menu item added successfully');
      setNewItem({ category_id: '', title: '', description: '', price: '', actual_price: '', image_url: '' });
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to add menu item');
    }
  };

  const handleUpdateMenuItem = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/menu-items/${editingItem.id}`, {
        ...editingItem,
        price: parseFloat(editingItem.price),
        actual_price: editingItem.actual_price ? parseFloat(editingItem.actual_price) : null
      });
      toast.success('Menu item updated successfully');
      setEditingItem(null);
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to update menu item');
    }
  };

  const handleDeleteMenuItem = async (id) => {
    try {
      await axios.delete(`${API}/menu-items/${id}`);
      toast.success('Menu item deleted');
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  // Finance functions
  const fetchFinance = async () => {
    try {
      const [recordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/finance`),
        axios.get(`${API}/finance/summary`)
      ]);
      setFinanceRecords(recordsRes.data);
      setFinanceSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
    }
  };

  const handleAddFinance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/finance`, {
        ...newFinance,
        amount: parseFloat(newFinance.amount)
      });
      toast.success('Finance record added');
      setNewFinance({ type: 'investment', amount: '', description: '' });
      fetchFinance();
    } catch (error) {
      toast.error('Failed to add finance record');
    }
  };

  // Stock functions
  const fetchStock = async () => {
    try {
      const [itemsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/stock`),
        axios.get(`${API}/stock/alerts`)
      ]);
      setStockItems(itemsRes.data);
      setStockAlerts(alertsRes.data);
    } catch (error) {
      console.error('Failed to fetch stock data:', error);
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/stock`, {
        ...newStock,
        quantity: parseInt(newStock.quantity),
        reorder_level: parseInt(newStock.reorder_level),
        price_per_unit: newStock.price_per_unit ? parseFloat(newStock.price_per_unit) : 0
      });
      toast.success('Stock item added and investment recorded');
      setNewStock({ item_name: '', quantity: '', unit: '', reorder_level: '', price_per_unit: '' });
      fetchStock();
      fetchFinance();
    } catch (error) {
      toast.error('Failed to add stock item');
    }
  };

  // Role functions
  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/roles`, {
        role_name: newRole.role_name,
        permissions: newRole.permissions.split(',').map(p => p.trim())
      });
      toast.success('Role added successfully');
      setNewRole({ role_name: '', permissions: '' });
      fetchRoles();
    } catch (error) {
      toast.error('Failed to add role');
    }
  };

  const handleDeleteRole = async (id) => {
    try {
      await axios.delete(`${API}/roles/${id}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  // User functions
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('User created successfully');
      setNewUser({ email: '', password: '', name: '', role: 'cashier' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await axios.delete(`${API}/users/${id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // Orders functions
  const fetchAllOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setAllOrders(response.data.sort((a, b) => b.order_number - a.order_number));
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  // Analytics functions
  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/top-items`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleClearAnalytics = async () => {
    if (!window.confirm('This will clear current analytics and save a snapshot. Continue?')) return;
    try {
      await axios.post(`${API}/analytics/clear`);
      toast.success('Analytics cleared and saved to history');
      fetchAnalytics();
      fetchAnalyticsHistory();
    } catch (error) {
      toast.error('Failed to clear analytics');
    }
  };

  const fetchAnalyticsHistory = async () => {
    try {
      const response = await axios.get(`${API}/analytics/history`);
      setAnalyticsHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics history:', error);
    }
  };

  // Survey functions
  const fetchSurveys = async () => {
    try {
      const response = await axios.get(`${API}/surveys`);
      setSurveys(response.data);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
    }
  };

  const handleAddSurvey = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/surveys`, {
        ...newSurvey,
        questions: newSurvey.questions.filter(q => q.trim() !== '')
      });
      toast.success('Survey created successfully');
      setNewSurvey({ title: '', questions: [''] });
      fetchSurveys();
    } catch (error) {
      toast.error('Failed to create survey');
    }
  };

  const addSurveyQuestion = () => {
    setNewSurvey({ ...newSurvey, questions: [...newSurvey.questions, ''] });
  };

  const updateSurveyQuestion = (index, value) => {
    const updatedQuestions = [...newSurvey.questions];
    updatedQuestions[index] = value;
    setNewSurvey({ ...newSurvey, questions: updatedQuestions });
  };

  const removeSurveyQuestion = (index) => {
    const updatedQuestions = newSurvey.questions.filter((_, i) => i !== index);
    setNewSurvey({ ...newSurvey, questions: updatedQuestions });
  };

  const handleArchiveSurvey = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'archived' : 'active';
      await axios.put(`${API}/surveys/${id}/status?status=${newStatus}`);
      toast.success(`Survey ${newStatus}`);
      fetchSurveys();
    } catch (error) {
      toast.error('Failed to update survey');
    }
  };

  const handleDeleteSurvey = async (id) => {
    try {
      await axios.delete(`${API}/surveys/${id}`);
      toast.success('Survey deleted');
      fetchSurveys();
    } catch (error) {
      toast.error('Failed to delete survey');
    }
  };

  const fetchSurveyResponses = async (surveyId) => {
    try {
      const response = await axios.get(`${API}/surveys/${surveyId}/responses`);
      setSurveyResponses({ ...surveyResponses, [surveyId]: response.data });
    } catch (error) {
      console.error('Failed to fetch responses:', error);
    }
  };


  // 👇 NEW FUNCTION: Handles moving items Up and Down
  const handleReorder = async (type, items, index, direction) => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Boundary check (cannot move first item up or last item down)
    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    // Swap items
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];

    // 1. Update UI immediately
    if (type === 'categories') setCategories(newItems);
    if (type === 'menu-items') setMenuItems(newItems);

    // 2. Save to Backend
    try {
      const endpoint = type === 'categories' ? '/categories/reorder' : '/menu-items/reorder';
      await axios.put(`${API}${endpoint}`, { ids: newItems.map(i => i.id) });
      toast.success('Order saved');
    } catch (error) {
      toast.error('Failed to save order');
      // Reload original data if failed
      if (type === 'categories') fetchCategories();
      if (type === 'menu-items') fetchMenuItems();
    }
  };

  // 👇 NEW: Sensors for Drag and Drop (Mouse & Touch)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 👇 NEW: Function that runs when you drop an item
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setMenuItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Save order to backend immediately
        axios.put(`${API}/menu-items/reorder`, {
          ids: newOrder.map(i => i.id)
        }).catch(() => toast.error('Failed to save order'));

        return newOrder;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 dark:bg-slate-950/85 border-b border-slate-200/80 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="font-sans text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50 flex items-center gap-2" data-testid="admin-dashboard-title">
              <LayoutGrid className="h-6 w-6 text-amber-500" />
              Admin Dashboard
            </h1>
            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Welcome, {user?.name}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={toggleTheme}
              variant="outline"
              size="icon"
              data-testid="admin-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              variant="outline"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 space-y-6">
        <Card className="mb-6 rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" />Shop Control</CardTitle>
            <CardDescription>Control whether users can create carts and make payments.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Badge className={shopOpen ? 'bg-green-600' : 'bg-red-600'}>{shopOpen ? 'Open' : 'Closed'}</Badge>
            <Button variant={shopOpen ? 'destructive' : 'default'} onClick={() => updateShopStatus(!shopOpen)}>
              {shopOpen ? 'Close Shop' : 'Open Shop'}
            </Button>
          </CardContent>
        </Card>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
          <TabsList className="w-full mb-2 flex flex-wrap gap-2 h-auto bg-transparent p-0">
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="menu" data-testid="tab-menu">Menu</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="promotions" data-testid="tab-promotions">Promotions</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="finance" data-testid="tab-finance">Finance</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="stock" data-testid="tab-stock">Stock</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="surveys" data-testid="tab-surveys">Surveys</TabsTrigger>
            <TabsTrigger className="rounded-full border border-slate-200 dark:border-slate-700 data-[state=active]:bg-slate-900 data-[state=active]:text-white dark:data-[state=active]:bg-amber-500 dark:data-[state=active]:text-slate-950" value="roles" data-testid="tab-roles">Access</TabsTrigger>
          </TabsList>

          {/* Menu Management Tab */}
          <TabsContent value="menu" className="space-y-6">
            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Add New Menu Item</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddMenuItem} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="item-category">Category</Label>
                      <Select
                        value={newItem.category_id}
                        onValueChange={(value) => setNewItem({ ...newItem, category_id: value })}
                        required
                      >
                        <SelectTrigger id="item-category" data-testid="item-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item-title">Title</Label>
                      <Input
                        id="item-title"
                        value={newItem.title}
                        onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                        required
                        data-testid="item-title-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="item-description">Description</Label>
                    <Textarea
                      id="item-description"
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      required
                      data-testid="item-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="item-price">Current Price (₹)</Label>
                      <Input
                        id="item-price"
                        type="number"
                        step="0.01"
                        value={newItem.price}
                        onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                        required
                        data-testid="item-price-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item-actual-price">Actual Price (₹) (Optional)</Label>
                      <Input
                        id="item-actual-price"
                        type="number"
                        step="0.01"
                        value={newItem.actual_price}
                        onChange={(e) => setNewItem({ ...newItem, actual_price: e.target.value })}
                        placeholder="e.g. 499"
                        data-testid="item-actual-price-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="item-image">Image URL</Label>
                      <Input
                        id="item-image"
                        value={newItem.image_url}
                        onChange={(e) => setNewItem({ ...newItem, image_url: e.target.value })}
                        placeholder="https://..."
                        data-testid="item-image-input"
                      />
                    </div>
                  </div>
                  <Button type="submit" data-testid="add-item-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Menu Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* 👇 NEW: Drag & Drop Wrapper */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={menuItems}
                      strategy={verticalListSortingStrategy}
                    >
                      {menuItems.map((item) => (
                        /* 👇 This uses the new component we created outside */
                        <SortableMenuItem
                          key={item.id}
                          item={item}
                          onEdit={setEditingItem}
                          onDelete={handleDeleteMenuItem}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Add New Category</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddCategory} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="category-name">Category Name</Label>
                    <Input
                      id="category-name"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      required
                      data-testid="category-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category-description">Description</Label>
                    <Input
                      id="category-description"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      data-testid="category-description-input"
                    />
                  </div>
                  <Button type="submit" data-testid="add-category-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {categories.map((category, index) => (
                    <div key={category.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`category-row-${category.name.toLowerCase()}`}>
                      <div className="flex items-center gap-3">
                        {/* 👇 NEW: Reorder Buttons */}
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost" size="icon" className="h-6 w-6"
                            disabled={index === 0}
                            onClick={() => handleReorder('categories', categories, index, 'up')}
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-6 w-6"
                            disabled={index === categories.length - 1}
                            onClick={() => handleReorder('categories', categories, index, 'down')}
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                        </div>
                        <div>
                          <h3 className="font-semibold">{category.name}</h3>
                          <p className="text-sm text-slate-600">{category.description}</p>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteCategory(category.id)}
                        data-testid={`delete-category-${category.name.toLowerCase()}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="promotions" className="space-y-6">
            <AdminPromotionsPanel />
          </TabsContent>

          <TabsContent value="orders" className="space-y-6">
            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>All Orders</CardTitle>
                <CardDescription>View all active and completed orders</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {allOrders.map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`admin-order-${order.order_number}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-mono text-lg font-bold" data-testid={`admin-order-number-${order.order_number}`}>
                            #{order.order_number}
                          </p>
                          <Badge
                            variant={
                              order.status === 'completed' ? 'secondary' :
                                order.status === 'cancelled' ? 'destructive' :
                                  order.status === 'ready' ? 'default' : 'outline'
                            }
                            className={
                              order.status === 'new' ? 'bg-amber-500' :
                                order.status === 'cooking' ? 'bg-blue-500' :
                                  order.status === 'ready' ? 'bg-green-500' : ''
                            }
                          >
                            {order.status}
                          </Badge>
                          <Badge variant="outline">
                            {order.payment_method === 'cash' ? 'Cash' : 'UPI'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-slate-600 dark:text-slate-400">Items:</p>
                            {order.items.map((item, idx) => (
                              <p key={idx}>{item.quantity}x {item.title}</p>
                            ))}
                          </div>
                          <div>
                            <p className="text-slate-600 dark:text-slate-400">Payment Details:</p>
                            <p>Total: <span className="font-bold">₹{order.total.toFixed(2)}</span></p>
                            {order.payment_method === 'cash' && order.amount_paid && (
                              <>
                                <p>Paid: ₹{order.amount_paid.toFixed(2)}</p>
                                <p>Change: ₹{(order.change || 0).toFixed(2)}</p>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                          {new Date(order.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {allOrders.length === 0 && (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                      <p className="text-slate-500">No orders yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Finance Tab */}
          <TabsContent value="finance" className="space-y-6">
            {financeSummary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Invested</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="total-invested">₹{financeSummary.total_invested.toFixed(2)}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold" data-testid="total-sales">₹{financeSummary.total_sales.toFixed(2)}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Profit</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${financeSummary.profit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="profit">
                      ₹{financeSummary.profit.toFixed(2)}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Add Investment</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddFinance} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="finance-type">Type</Label>
                      <Select
                        value={newFinance.type}
                        onValueChange={(value) => setNewFinance({ ...newFinance, type: value })}
                      >
                        <SelectTrigger id="finance-type" data-testid="finance-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="investment">Investment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="finance-amount">Amount (₹)</Label>
                      <Input
                        id="finance-amount"
                        type="number"
                        step="0.01"
                        value={newFinance.amount}
                        onChange={(e) => setNewFinance({ ...newFinance, amount: e.target.value })}
                        required
                        data-testid="finance-amount-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="finance-description">Description</Label>
                    <Input
                      id="finance-description"
                      value={newFinance.description}
                      onChange={(e) => setNewFinance({ ...newFinance, description: e.target.value })}
                      required
                      data-testid="finance-description-input"
                    />
                  </div>
                  <Button type="submit" data-testid="add-finance-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Record
                  </Button>
                </form>
              </CardContent>
            </Card>

            {financeSummary?.item_sales && financeSummary.item_sales.length > 0 && (
              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                <CardHeader>
                  <CardTitle>Item-wise Sales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={financeSummary.item_sales.slice(0, 10)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="title" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="revenue" fill="#f59e0b" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Investment & Expense History</CardTitle>
                <CardDescription>List of all manually added investments and stock purchases.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-0 border rounded-lg overflow-hidden">
                  <div className="grid grid-cols-4 gap-4 p-4 bg-slate-100 dark:bg-slate-800 font-bold text-sm">
                    <div className="col-span-2">Description</div>
                    <div>Date</div>
                    <div className="text-right">Amount</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {financeRecords.length === 0 ? (
                       <p className="p-8 text-center text-slate-500">No records found.</p>
                    ) : (
                      financeRecords.map((record, index) => (
                        <div key={record.id || index} className="grid grid-cols-4 gap-4 p-4 border-t text-sm hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors">
                          <div className="col-span-2">
                            <p className="font-medium">{record.description}</p>
                            <Badge variant="outline" className="mt-1 text-xs">{record.type}</Badge>
                          </div>
                          <div className="text-slate-500 flex items-center">
                            {new Date(record.date).toLocaleDateString()}
                          </div>
                          <div className="text-right font-bold text-red-500">
                             -₹{parseFloat(record.amount).toFixed(2)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
                        
          </TabsContent>

          {/* Stock Tab */}
          <TabsContent value="stock" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-sm">Empty Stock</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600" data-testid="empty-stock-count">{stockAlerts.empty.length}</div>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-sm">Low Stock</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600" data-testid="low-stock-count">{stockAlerts.low_stock.length}</div>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-sm">Reorder Needed</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-600" data-testid="reorder-count">{stockAlerts.reorder_needed.length}</div>
                </CardContent>
              </Card>
            </div>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Add Stock Item</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddStock} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="stock-name">Item Name</Label>
                      <Input
                        id="stock-name"
                        value={newStock.item_name}
                        onChange={(e) => setNewStock({ ...newStock, item_name: e.target.value })}
                        required
                        data-testid="stock-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="stock-quantity">Quantity</Label>
                      <Input
                        id="stock-quantity"
                        type="number"
                        value={newStock.quantity}
                        onChange={(e) => setNewStock({ ...newStock, quantity: e.target.value })}
                        required
                        data-testid="stock-quantity-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="stock-unit">Unit</Label>
                      <Input
                        id="stock-unit"
                        value={newStock.unit}
                        onChange={(e) => setNewStock({ ...newStock, unit: e.target.value })}
                        placeholder="kg, liters, pieces..."
                        required
                        data-testid="stock-unit-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="stock-reorder">Reorder Level</Label>
                      <Input
                        id="stock-reorder"
                        type="number"
                        value={newStock.reorder_level}
                        onChange={(e) => setNewStock({ ...newStock, reorder_level: e.target.value })}
                        required
                        data-testid="stock-reorder-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="stock-price">Price per Unit (₹)</Label>
                      <Input
                        id="stock-price"
                        type="number"
                        step="0.01"
                        value={newStock.price_per_unit}
                        onChange={(e) => setNewStock({ ...newStock, price_per_unit: e.target.value })}
                        placeholder="Optional"
                        data-testid="stock-price-input"
                      />
                    </div>
                  </div>
                  <Button type="submit" data-testid="add-stock-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stock Item
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Stock Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {stockItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`stock-item-${item.item_name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div>
                        <h3 className="font-semibold">{item.item_name}</h3>
                        <p className="text-sm text-slate-600">
                          {item.quantity} {item.unit} (Reorder at: {item.reorder_level})
                          {item.price_per_unit > 0 && ` • ₹${item.price_per_unit}/${item.unit}`}
                        </p>
                      </div>
                      <Badge variant={item.quantity === 0 ? 'destructive' : item.quantity <= item.reorder_level ? 'secondary' : 'default'}>
                        {item.quantity === 0 ? 'Empty' : item.quantity <= item.reorder_level ? 'Low' : 'In Stock'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="flex justify-end mb-4">
              <Button
                onClick={handleClearAnalytics}
                variant="outline"
                data-testid="clear-analytics-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear & Save Analytics
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                <CardHeader>
                  <CardTitle>Most Ordered Items</CardTitle>
                  <CardDescription>Top 10 items by order quantity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.most_ordered.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`top-ordered-${index}`}>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-amber-500 text-black">{index + 1}</Badge>
                          <div>
                            <p className="font-semibold">{item.title}</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              Revenue: ₹{item.revenue.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline">{item.count} orders</Badge>
                      </div>
                    ))}
                    {analytics.most_ordered.length === 0 && (
                      <p className="text-center text-slate-500 py-8">No order data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                <CardHeader>
                  <CardTitle>Most Favorited Items</CardTitle>
                  <CardDescription>Top 10 items by favorites</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analytics.most_favorited.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`top-favorited-${index}`}>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-red-500 text-white">{index + 1}</Badge>
                          <p className="font-semibold">{item.title}</p>
                        </div>
                        <Badge variant="outline">{item.count} favorites</Badge>
                      </div>
                    ))}
                    {analytics.most_favorited.length === 0 && (
                      <p className="text-center text-slate-500 py-8">No favorites data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {analyticsHistory.length > 0 && (
              <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
                <CardHeader>
                  <CardTitle>Analytics History</CardTitle>
                  <CardDescription>Previously saved analytics snapshots</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analyticsHistory.map((snapshot, idx) => (
                      <div key={snapshot.id} className="border rounded-lg p-4" data-testid={`analytics-snapshot-${idx}`}>
                        <p className="text-sm font-semibold mb-2">
                          Snapshot from {new Date(snapshot.created_at).toLocaleString()}
                        </p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-medium mb-1">Top Ordered:</p>
                            {snapshot.most_ordered.slice(0, 3).map((item, i) => (
                              <p key={i} className="text-slate-600 dark:text-slate-400">
                                {i + 1}. {item.title} ({item.count})
                              </p>
                            ))}
                          </div>
                          <div>
                            <p className="font-medium mb-1">Top Favorited:</p>
                            {snapshot.most_favorited.slice(0, 3).map((item, i) => (
                              <p key={i} className="text-slate-600 dark:text-slate-400">
                                {i + 1}. {item.title} ({item.count})
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Surveys Tab */}
          <TabsContent value="surveys" className="space-y-6">
            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Create New Survey</CardTitle>
                <CardDescription>Design customer feedback surveys</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddSurvey} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="survey-title">Survey Title</Label>
                    <Input
                      id="survey-title"
                      value={newSurvey.title}
                      onChange={(e) => setNewSurvey({ ...newSurvey, title: e.target.value })}
                      placeholder="e.g., Customer Satisfaction Survey"
                      required
                      data-testid="survey-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Questions</Label>
                    {newSurvey.questions.map((question, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={question}
                          onChange={(e) => updateSurveyQuestion(index, e.target.value)}
                          placeholder={`Question ${index + 1}`}
                          data-testid={`survey-question-${index}`}
                        />
                        {newSurvey.questions.length > 1 && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removeSurveyQuestion(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSurveyQuestion}
                      data-testid="add-question-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Question
                    </Button>
                  </div>
                  <Button type="submit" data-testid="create-survey-button">
                    Create Survey
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>All Surveys</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {surveys.map((survey) => (
                    <div key={survey.id} className="p-4 border rounded-lg" data-testid={`survey-${survey.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{survey.title}</h3>
                          <Badge variant={survey.status === 'active' ? 'default' : 'secondary'}>
                            {survey.status}
                          </Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fetchSurveyResponses(survey.id)}
                            data-testid={`view-responses-${survey.id}`}
                          >
                            View Responses ({surveyResponses[survey.id]?.length || 0})
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleArchiveSurvey(survey.id, survey.status)}
                            data-testid={`archive-survey-${survey.id}`}
                          >
                            {survey.status === 'active' ? 'Archive' : 'Activate'}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteSurvey(survey.id)}
                            data-testid={`delete-survey-${survey.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {survey.questions.map((q, idx) => (
                          <p key={idx} className="text-sm text-slate-600 dark:text-slate-400">
                            {idx + 1}. {q}
                          </p>
                        ))}
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Created: {new Date(survey.created_at).toLocaleDateString()}
                      </p>

                      {/* Show responses if loaded */}
                      {surveyResponses[survey.id] && surveyResponses[survey.id].length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-semibold mb-2">Responses:</h4>
                          <div className="space-y-3 max-h-64 overflow-y-auto">
                            {surveyResponses[survey.id].map((response, rIdx) => (
                              <div key={response.id} className="bg-slate-50 dark:bg-slate-800 p-3 rounded text-sm">
                                <p className="font-medium mb-2">Response #{rIdx + 1}</p>
                                {Object.entries(response.answers).map(([qIdx, answer]) => (
                                  <div key={qIdx} className="mb-2">
                                    <p className="text-xs text-slate-600 dark:text-slate-400">Q{parseInt(qIdx) + 1}:</p>
                                    <p className="text-slate-800 dark:text-slate-200">{answer}</p>
                                  </div>
                                ))}
                                <p className="text-xs text-slate-500 mt-2">
                                  {new Date(response.created_at).toLocaleString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {surveys.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No surveys yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles" className="space-y-6">
            {/* User Management Section */}
            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Create New User</CardTitle>
                <CardDescription>Add a new user and assign them a role</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="user-name">Name</Label>
                      <Input
                        id="user-name"
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        required
                        data-testid="user-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-email">Email</Label>
                      <Input
                        id="user-email"
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        required
                        data-testid="user-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="user-password">Password</Label>
                      <Input
                        id="user-password"
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        required
                        data-testid="user-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-role">Role</Label>
                      <Select
                        value={newUser.role}
                        onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                      >
                        <SelectTrigger id="user-role" data-testid="user-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="cashier">Cashier</SelectItem>
                          <SelectItem value="cook">Cook</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button type="submit" data-testid="add-user-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Create User
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>All Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`user-row-${user.email.replace('@', '-').replace('.', '-')}`}>
                      <div>
                        <h3 className="font-semibold">{user.name}</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{user.email}</p>
                        <Badge variant="outline" className="mt-1">{user.role}</Badge>
                      </div>
                      {user.email !== 'pravith1p93@gmail.com' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          data-testid={`delete-user-${user.email.replace('@', '-').replace('.', '-')}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Custom Role Permissions</CardTitle>
                <CardDescription>Define custom roles with specific permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddRole} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="role-name">Role Name</Label>
                    <Input
                      id="role-name"
                      value={newRole.role_name}
                      onChange={(e) => setNewRole({ ...newRole, role_name: e.target.value })}
                      required
                      data-testid="role-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role-permissions">Permissions (comma-separated)</Label>
                    <Input
                      id="role-permissions"
                      value={newRole.permissions}
                      onChange={(e) => setNewRole({ ...newRole, permissions: e.target.value })}
                      placeholder="read_orders, write_orders, manage_menu"
                      required
                      data-testid="role-permissions-input"
                    />
                  </div>
                  <Button type="submit" data-testid="add-role-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Role
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-slate-200/80 dark:border-slate-800 shadow-sm">
              <CardHeader>
                <CardTitle>Custom Roles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {roles.map((role) => (
                    <div key={role.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`role-row-${role.role_name.toLowerCase()}`}>
                      <div>
                        <h3 className="font-semibold">{role.role_name}</h3>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {role.permissions.map((perm, idx) => (
                            <Badge key={idx} variant="outline">{perm}</Badge>
                          ))}
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteRole(role.id)}
                        data-testid={`delete-role-${role.role_name.toLowerCase()}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Item Dialog */}
      {editingItem && (
        <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
          <DialogContent data-testid="edit-item-dialog">
            <DialogHeader>
              <DialogTitle>Edit Menu Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateMenuItem} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingItem.title}
                  onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingItem.description}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Current Price (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.price}
                    onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Actual Price (₹) (Optional)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingItem.actual_price || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, actual_price: e.target.value })}
                    placeholder="e.g. 499"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Image URL</Label>
                <Input
                  value={editingItem.image_url || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, image_url: e.target.value })}
                  placeholder="https://..."
                />
                {editingItem.image_url && (
                  <img
                    src={editingItem.image_url}
                    alt="Preview"
                    className="h-16 w-16 object-cover rounded mt-2 border"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Available</Label>
                <Select
                  value={editingItem.available.toString()}
                  onValueChange={(value) => setEditingItem({ ...editingItem, available: value === 'true' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Available</SelectItem>
                    <SelectItem value="false">Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit">Update Item</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
